<template>
  <Layout class="md:mt-32 lg:mt-16">
    <section class="flex flex-col gap-7 text-left">
      <div class="-mb-1 flex items-center justify-between">
        <h1 class="text-left text-2xl text-dark-blue">
          Email Verification Pending...
        </h1>
        <img
          class="-mt-5"
          src="@/assets/highway-badge.svg"
          alt="Highway Badge"
        />
      </div>
      <p class="text-dark-gray">
        We just sent an email to
        {{ store.state.users.currentUser?.email || '' }} so you can verify your
        email address. Click on the link in that email and then return here to
        continue.
      </p>
      <p class="text-xs text-body-color">
        Email didn't send?
        <span
          class="cursor-pointer text-yellow-500 hover:text-yellow-600"
          @click="handleSubmit"
        >
          Try again
        </span>
      </p>
    </section>
  </Layout>
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed } from 'vue';
import { useIdentityVerification } from '@/composables/useIdentityVerification';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Layout from '@/components/layouts/HoldingTankLayout.vue';
import { useToast } from 'vue-toast-notification';

const store = useStore();
const toast = useToast();
const router = useRouter();
const { redirectBasedOnCompanyType } = useIdentityVerification();

const redirectAfterIDV = computed(
  () => store.getters['identityVerification/getRedirectAfterIDV']
);

const companyType = computed(
  () => store.state.users.currentUser?.company?.company_type
);

const isEmpty = (value) => value == null || value === '';

const handleRedirect = () => {
  const { name, path } = redirectAfterIDV.value || {};
  console.log('Redirecting to:', name, path);

  console.log('Redirecting to:', redirectAfterIDV.value);
  if (name) {
    console.log('Redirecting using name');
    return router.push({ name });
  }

  if (path) {
    console.log('Redirecting using path');
    return router.push(path);
  }

  if (companyType.value) {
    console.log('Redirecting using company id');
    return redirectBasedOnCompanyType(companyType.value);
  }
  if (
    isEmpty(redirectAfterIDV.value?.name) &&
    isEmpty(redirectAfterIDV.value?.path)
  ) {
    console.log('Validating code');
    validateLogic();
  }
  if (!store.state.users.currentUser.company?.id) {
    console.log('Redirecting to account verification');
    return router.push({ name: 'FMSCAAccountVerification' });
  }
};

const handleSubmit = async () => {
  try {
    const res = await store.dispatch(
      'users/updateEmail',
      store.state.users.currentUser.email
    );

    if ([200, 201, 204].includes(res.status)) {
      toast?.open({
        type: 'info',
        message: 'Email resent',
        position: 'top-right',
        duration: 5000,
      });
    }
  } catch (error) {
    console.error('Error sending validation email:', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const startPolling = () => {
  const interval = setInterval(() => {
    store.dispatch('users/serverRefreshCurrentUser').then(() => {
      if (store.state.users.currentUser.is_verified_email) {
        clearInterval(interval);
        handleRedirect();
      }
    });
  }, 5000);

  onBeforeUnmount(() => {
    clearInterval(interval);
  });
};

const isChallengeRequired = computed(
  () => store.state.verifications?.challenge_question_required || false
);

const isEmailRequiredForLogin = computed(
  () => store.state.carriers?.isEmailRequiredForLogin || false
);

const handleBigCarrierSubmit = async () => {
  const res = await store.dispatch('users/hasCarrierBeenClaimed');
  if (res.status === 200) {
    store.commit('carriers/setMaskedContacts', res.data);
  }
  if (isChallengeRequired.value === true) {
    router.push({
      name: 'VerificationChallenge',
    });
  } else if (res.status === 200) {
    router.push({
      name: 'RequestAccessFMSCA',
      params: { carrierHasBeenClaimed: true },
    });
  } else if (res.status === 404) {
    if (isChallengeRequired.value === true) {
      router.push({
        name: 'VerificationChallenge',
      });
    } else {
      router.push({
        name: 'FMSCAAccountVerification',
        params: { canMoveOn: true },
      });
    }
  } else {
    toast?.open({
      type: 'error',
      message: res?.message || 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const validateLogic = async () => {
  const res = await store.dispatch('users/hasCarrierBeenClaimed');
  if (isEmailRequiredForLogin.value) {
    await handleBigCarrierSubmit();
    return;
  }
  if (isChallengeRequired.value === true) {
    router.push({
      name: 'VerificationChallenge',
    });
  } else {
    if (res.status === 200) {
      store.commit('carriers/setMaskedContacts', res.data);
    }
    if (res.status === 200) {
      router.push({
        name: 'RequestAccessFMSCA',
        params: { carrierHasBeenClaimed: true },
      });
    } else if (res.status === 404) {
      router.push({
        name: 'FMSCAAccountVerification',
        params: { canMoveOn: true },
      });
    } else {
      toast?.open({
        type: 'error',
        message: res?.message || 'An error occurred. Please try again.',
        position: 'top-right',
        duration: 5000,
      });
    }
  }
};

onMounted(() => {
  if (store.state.users.currentUser?.is_verified_email) {
    handleRedirect();
  }
  startPolling();
});
</script>
